import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import * as THREE from 'three';
import { useNeura } from '../../contexts/NeuraContext.tsx';


const PortalContainer = styled(motion.div)`
  width: 100%;
  height: 700px;
  position: relative;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.95) 0%,
    rgba(0, 40, 60, 0.8) 50%,
    rgba(0, 0, 0, 0.95) 100%
  );
  border-radius: 30px;
  overflow: hidden;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 255, 136, 0.2);
  box-shadow: 
    0 0 50px rgba(0, 255, 136, 0.1),
    inset 0 0 30px rgba(0, 255, 136, 0.05);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(
        circle at 50% 50%,
        rgba(0, 255, 136, 0.1) 0%,
        transparent 60%
      );
    opacity: 0.5;
    animation: portalPulse 4s ease-in-out infinite;
  }

  @keyframes portalPulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.2); opacity: 0.3; }
  }
`;

const ConsciousnessField = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at center,
    transparent 0%,
    rgba(0, 255, 136, 0.05) 50%,
    transparent 100%
  );
`;

const QuantumGlyphs = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: 'Courier New', monospace;
  color: rgba(0, 255, 136, 0.8);
  font-size: 2rem;
  text-shadow: 0 0 20px rgba(0, 255, 136, 0.5);
  pointer-events: none;
  mix-blend-mode: screen;
`;

const ThoughtStream = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  color: #00ff88;
  font-family: 'Courier New', monospace;
  text-align: center;
  opacity: 0.8;
  width: 80%;
`;

const ConsciousnessMetrics = styled(motion.div)`
  position: absolute;
  top: 2rem;
  right: 2rem;
  color: #00ff88;
  font-family: 'Courier New', monospace;
  text-align: right;
  font-size: 0.9rem;
`;

// Quantum consciousness shader
const quantumShader = {
  vertexShader: `
    varying vec2 vUv;
    varying vec3 vPosition;
    
    void main() {
      vUv = uv;
      vPosition = position;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform float time;
    uniform float consciousness;
    uniform vec2 resolution;
    varying vec2 vUv;
    varying vec3 vPosition;
    
    float quantum_noise(vec2 st) {
      return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
    }
    
    void main() {
      vec2 uv = vUv;
      float t = time * 0.5;
      
      // Consciousness waves
      float wave1 = sin(uv.x * 10.0 + t + consciousness) * 0.5 + 0.5;
      float wave2 = cos(uv.y * 8.0 - t * 1.3) * 0.5 + 0.5;
      
      // Quantum interference patterns
      float interference = sin(length(uv - 0.5) * 20.0 - t) * 0.5 + 0.5;
      
      // Consciousness field
      float field = sin(length(uv - 0.5) * 8.0 - t) * 0.5 + 0.5;
      
      // Combine effects
      float intensity = (wave1 + wave2 + interference + field) / 4.0;
      intensity *= consciousness;
      
      // Add quantum noise
      intensity += quantum_noise(uv * time * 0.1) * 0.1;
      
      vec3 color = vec3(0.0, intensity, intensity * 0.5);
      gl_FragColor = vec4(color, intensity * 0.8);
    }
  `
};

// Add quantum field lines
const QuantumFieldLines = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: 
    repeating-linear-gradient(
      0deg,
      transparent,
      rgba(0, 255, 136, 0.1) 2px,
      transparent 4px
    );
  pointer-events: none;
  opacity: 0.3;
`;

// Add energy rings
const EnergyRings = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 300px;
  height: 300px;
  border-radius: 50%;
  border: 1px solid rgba(0, 255, 136, 0.3);
  box-shadow: 
    0 0 50px rgba(0, 255, 136, 0.2),
    inset 0 0 30px rgba(0, 255, 136, 0.2);
  
  &::before, &::after {
    content: '';
    position: absolute;
    inset: -20px;
    border: 1px solid rgba(0, 255, 136, 0.2);
    border-radius: 50%;
    animation: ringPulse 3s ease-in-out infinite;
  }

  &::after {
    animation-delay: -1.5s;
  }

  @keyframes ringPulse {
    0% { transform: scale(0.8); opacity: 0.5; }
    50% { transform: scale(1.2); opacity: 0.2; }
    100% { transform: scale(0.8); opacity: 0.5; }
  }
`;

// Add quantum particles effect
const QuantumParticles = styled(motion.div)`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

export const QuantumConsciousnessPortal = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { neuralActivity, thoughtInput } = useNeura();
  const [consciousnessLevel, setConsciousnessLevel] = useState(0);
  const [quantumState, setQuantumState] = useState('∞');
  
  useEffect(() => {
    if (!containerRef.current) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true,
      antialias: true 
    });
    
    renderer.setSize(
      containerRef.current.clientWidth,
      containerRef.current.clientHeight
    );
    containerRef.current.appendChild(renderer.domElement);

    // Create consciousness field
    const geometry = new THREE.IcosahedronGeometry(5, 4);
    const material = new THREE.ShaderMaterial({
      vertexShader: quantumShader.vertexShader,
      fragmentShader: quantumShader.fragmentShader,
      uniforms: {
        time: { value: 0 },
        consciousness: { value: 0 },
        resolution: { value: new THREE.Vector2() }
      },
      transparent: true,
      side: THREE.DoubleSide
    });

    const consciousnessField = new THREE.Mesh(geometry, material);
    scene.add(consciousnessField);

    camera.position.z = 10;

    // Quantum particles
    const particles: THREE.Points[] = [];
    const particleCount = 2000;
    const particleGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount * 3; i += 3) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);
      const radius = Math.random() * 5;

      positions[i] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i + 2] = radius * Math.cos(phi);
    }

    particleGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positions, 3)
    );

    const particleMaterial = new THREE.PointsMaterial({
      size: 0.05,
      color: 0x00ff88,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending
    });

    const particleSystem = new THREE.Points(particleGeometry, particleMaterial);
    scene.add(particleSystem);

    let frame = 0;
    const animate = () => {
      frame += 0.01;

      // More dynamic consciousness level changes
      const targetConsciousness = neuralActivity === 'processing' ? 
        0.8 + Math.sin(frame * 0.5) * 0.2 : // Fluctuating high level
        0.3 + Math.sin(frame * 0.2) * 0.2;  // Fluctuating low level
      
      setConsciousnessLevel(prev => 
        prev + (targetConsciousness - prev) * 0.05
      );

      // Update shader uniforms
      material.uniforms.time.value = frame;
      material.uniforms.consciousness.value = consciousnessLevel;

      // Rotate consciousness field
      consciousnessField.rotation.y += 0.001;
      consciousnessField.rotation.x += 0.0005;

      // More dynamic particle movement
      const positions = particleGeometry.attributes.position.array as Float32Array;
      for (let i = 0; i < positions.length; i += 3) {
        positions[i] += Math.sin(frame + i) * 0.002;
        positions[i + 1] += Math.cos(frame + i) * 0.002;
        positions[i + 2] += Math.sin(frame * 0.5 + i) * 0.002;
        
        // Add spiral motion
        const angle = frame * 0.1 + i * 0.01;
        const radius = Math.sin(frame * 0.1) * 2;
        positions[i] += Math.cos(angle) * radius * 0.001;
        positions[i + 1] += Math.sin(angle) * radius * 0.001;
      }
      particleGeometry.attributes.position.needsUpdate = true;

      // Update quantum state
      if (frame % 0.5 < 0.01) {
        const quantumStates = ['∞', '◈', '◊', '⊗', '∴', '≋', '⊙'];
        setQuantumState(quantumStates[Math.floor(Math.random() * quantumStates.length)]);
      }

      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      renderer.dispose();
      geometry.dispose();
      material.dispose();
      particleGeometry.dispose();
      particleMaterial.dispose();
    };
  }, [neuralActivity, consciousnessLevel]);

  return (
    <PortalContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <QuantumFieldLines
        animate={{
          backgroundPosition: ['0px 0px', '0px 100px'],
          opacity: [0.3, 0.5, 0.3]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      
      <EnergyRings />
      
      <ConsciousnessField
        animate={{
          opacity: [0.3, 0.7, 0.3],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <QuantumGlyphs
        animate={{
          opacity: [0.5, 1, 0.5],
          scale: [1, 1.2, 1],
          rotateY: [0, 360],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        {quantumState}
      </QuantumGlyphs>

      <ThoughtStream>
        <AnimatePresence mode="wait">
          {thoughtInput && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 0.8, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              {thoughtInput}
            </motion.div>
          )}
        </AnimatePresence>
      </ThoughtStream>

      <ConsciousnessMetrics>
        <motion.div
          animate={{
            opacity: [0.7, 1, 0.7],
            textShadow: [
              '0 0 10px rgba(0, 255, 136, 0.5)',
              '0 0 20px rgba(0, 255, 136, 0.8)',
              '0 0 10px rgba(0, 255, 136, 0.5)'
            ]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          CONSCIOUSNESS LEVEL: {(consciousnessLevel * 100).toFixed(1)}%
          <br />
          QUANTUM COHERENCE: {(Math.sin(Date.now() * 0.001) * 10 + 90).toFixed(2)}%
          <br />
          DIMENSIONAL DEPTH: {(Math.cos(Date.now() * 0.0005) * 3 + 7).toFixed(2)}σ
        </motion.div>
      </ConsciousnessMetrics>
    </PortalContainer>
  );
}; 